import { useI18n, Trans, useApplicationContext } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  Alert,
  Card,
  Col,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
  Flex,
} from '@wyz/ui';
import { DateUtils, mapArrayToObject } from '@wyz/utils';
import { isNil } from 'lodash-es';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useManageCustomers, useManageUsers } from '~/common/api';
import {
  CriteriaBuilder,
  MatchedCustomersTable,
  DateField,
  SelectField,
} from '~/common/components';
import { PAGINATE_ALL_LIMIT, Validations } from '~/common/helpers';
import { useMyCustomers } from '~/common/hooks';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { usePricingPolicyForm } from '~/purchase/components';
import { RedistributionPricingPolicyHelpers } from '~/purchase/helpers';
import {
  PRICING_STATUS_ENUM,
  redistributionPricingApplicationStepFormSchema,
} from '~/purchase/schemas';
import {
  IGlobalRedistributionPricingFormData,
  IPricingStatus,
  IRedistributionPricingApplicationStepForm,
} from '~/purchase/types';
import { ENTITIES_STATUSES, FEATURE_CONTEXT } from '~/common/schemas';

type CommonFormProps = {
  globalFormData: IGlobalRedistributionPricingFormData;
  policyGroupId?: number;
};

type ApplicationStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IRedistributionPricingApplicationStepForm,
    status: IPricingStatus,
  ) => void;
};

const ApplicationStepForm: React.FC<ApplicationStepFormProps> = ({
  onSubmit,
  globalFormData,
}) => {
  const { t } = useI18n();
  const pricingPolicyForm = usePricingPolicyForm();
  const methods = useForm<IRedistributionPricingApplicationStepForm>({
    defaultValues: globalFormData.APPLICATION,
    resolver: zodResolver(redistributionPricingApplicationStepFormSchema),
  });
  const watchedStartDate = useWatch({
    control: methods.control,
    name: 'startDate',
  });

  const { auth } = useApplicationContext();
  const getCustomersByUser = useMyCustomers();
  const watchedSupplierIds = useWatch({
    control: methods.control,
    name: 'supplierIds',
  });

  const manageUserCustomers = useManageUsers({
    paginateCustomersByUser: {
      args: {
        ids: watchedSupplierIds,
        userId: auth.userId as string,
      },
      enabled: !!watchedSupplierIds?.length,
      refetchOnMount: true,
    },
  });

  const userCustomersById = mapArrayToObject(
    manageUserCustomers.paginateCustomersByUser.data?.data ?? [],
    'id',
  );

  const selectedWyzApiSupplierIds = watchedSupplierIds.reduce(
    (internalStockWyzApiIds, id) => {
      const supplier = userCustomersById[id];
      if (supplier) {
        internalStockWyzApiIds = internalStockWyzApiIds.concat(
          supplier.internalStockIds,
        );
      }
      return internalStockWyzApiIds;
    },
    [] as Array<number>,
  );

  const watchedCriteria = useWatch({
    control: methods.control,
    name: 'customersCriteria',
  });

  const manageCustomers = useManageCustomers({
    validateMatchedCustomersCurrency: { enabled: true },
  });

  return (
    <FormContainer
      className='space-y-4'
      methods={methods}
      submitHandler={(formData, event) => {
        manageCustomers.validateMatchedCustomersCurrency.mutate(
          {
            page: 1,
            limit: PAGINATE_ALL_LIMIT,
            statuses: [ENTITIES_STATUSES.ENABLED],
            criteria: watchedCriteria.map((associatedCriterion) => ({
              criterion: associatedCriterion.criterion.id,
              values: associatedCriterion.values,
            })),
            featureContext:
              FEATURE_CONTEXT.CUSTOMERS_REDISTRIBUTION_PRICING_POLICY,
            matchUserContext: true,
            parentIds: watchedSupplierIds,
          },
          {
            onSuccess: () =>
              onSubmit(
                formData,
                ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
                  'data-status',
                ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
              ),
            onError: (error) =>
              Validations.setFormErrors({
                setError: methods.setError,
                errors: Validations.formatValidationErrors(error.errors, t),
              }),
          },
        );
      }}
      id={RedistributionPricingPolicyHelpers.REDISTRIBUTION_PRICING_POLICY_STEP_INDEX.APPLICATION.toString()}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
    >
      <Alert variant='info'>
        <Flex>
          <Icon icon='information-line' />
          <div>
            <Text weight='bolder' color='info'>
              <Trans i18nKey='app.pricing_policy.redistribution.form.application_step.info.title' />
            </Text>
            <Text weight='normal' color='info'>
              <Trans i18nKey='app.pricing_policy.redistribution.form.application_step.info.subtitle' />
            </Text>
          </div>
        </Flex>
      </Alert>
      <Card>
        <Card.Body>
          <Row>
            <Col sm={12} md={6}>
              <DateField
                name='startDate'
                label={
                  <Trans i18nKey='app.pricing_policy.redistribution.form.application.start_date' />
                }
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            {watchedStartDate && (
              <Col sm={12} md={6}>
                <DateField
                  name='endDate'
                  label={
                    <Trans i18nKey='app.pricing_policy.redistribution.form.application.end_date' />
                  }
                  dateFormat={DateUtils.DATE_FORMAT.DMY}
                  placeholderText='dd-MM-yyyy'
                  showTimeSelect={false}
                  minDate={
                    isNil(watchedStartDate) ? undefined : watchedStartDate
                  }
                  translations={{
                    applyButton: t('app.common.button.apply'),
                    resetButton: t('app.common.button.reset'),
                  }}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Accordion.Container flush defaultActiveKey='0'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                <Title heading='xl' weight='bolder'>
                  <Trans i18nKey='app.pricing_policy.redistribution.form.application.suppliers.title' />
                </Title>
              </Accordion.Header>
              <Accordion.Body>
                <SelectField<true>
                  name='supplierIds'
                  label={
                    <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.form.customers' />
                  }
                  options={
                    getCustomersByUser.data?.data
                      .filter((item) => item.level === CUSTOMER_LEVEL.N1)
                      .map((item) => ({
                        label: `${item.name} (${item.code})`,
                        value: item.id,
                      })) || []
                  }
                  isMulti={true}
                  maxShown={Infinity}
                  wrapItems
                  required
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion.Container>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Accordion.Container flush defaultActiveKey='0'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                <Title heading='xl' weight='bolder'>
                  <Trans i18nKey='app.pricing_policy.redistribution.form.application.customers.title' />
                </Title>
              </Accordion.Header>
              <Accordion.Body>
                <CriteriaBuilder
                  name='customersCriteria'
                  visibilityFilter={{ customerIds: watchedSupplierIds }}
                >
                  <Row className='mt-4'>
                    <Col md={12}>
                      <MatchedCustomersTable
                        featureContext={
                          FEATURE_CONTEXT.CUSTOMERS_REDISTRIBUTION_PRICING_POLICY
                        }
                        name={'customersCriteria'}
                        matchUserContext
                        queryOverrideParams={{
                          parentIds: watchedSupplierIds,
                        }}
                        renderCode={(item) => {
                          for (const supplierId of selectedWyzApiSupplierIds) {
                            if (item.supplierDmsRefDictionary[supplierId]) {
                              return item.supplierDmsRefDictionary[supplierId];
                            }
                          }
                          return item.configuration.dmsRef || item.code;
                        }}
                      />
                    </Col>
                  </Row>
                </CriteriaBuilder>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion.Container>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default ApplicationStepForm;
